import React, { FC } from 'react';
import styled from 'styled-components';
import { Icon } from '../icon';
import Stack from '../stack/stack';
import { SkeletonBlock } from '../skeleton';
import { T } from 'src/shared/helpers';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';

export const AdminToolGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
    margin-top: 12px;
`;

const ToolCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-height: 220px;
    padding: 22px;
    background: #ffffff;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    color: ${(props) => props.theme.app.textColor};
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        transform 0.15s ease;

    &:hover {
        border-color: ${(props) => props.theme.app.secondary};
        box-shadow: 0 8px 24px rgba(38, 50, 56, 0.1);
        transform: translateY(-2px);
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 2px;
    }
`;

const ToolContent = styled(Stack)`
    width: 100%;
    height: 100%;
`;

const ToolIcon = styled(Icon)`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: #fef2ed;
    color: ${(props) => props.theme.app.secondary};

    svg {
        width: 22px;
        height: 22px;
    }
`;

const ToolCategory = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(props) => props.theme.app.secondary};
`;

const ToolTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
    color: ${(props) => props.theme.app.primary_dark};
`;

const ToolDescription = styled.p`
    flex: 1;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${(props) => props.theme.app.primary};
`;

const ToolAction = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.secondary};
`;

interface AdminToolCardProps {
    category: string;
    title: string;
    description: string;
    action: string;
    icon: React.ReactNode;
    onClick: () => void;
}

export const AdminToolCard: FC<AdminToolCardProps> = ({ category, title, description, action, icon, onClick }) => (
    <ToolCard type='button' onClick={onClick}>
        <ToolContent $gap='12px'>
            <ToolIcon>{icon}</ToolIcon>
            <ToolCategory>{category}</ToolCategory>
            <ToolTitle>{title}</ToolTitle>
            <ToolDescription>{description}</ToolDescription>
            <ToolAction>
                {action}
                <Icon>
                    <ArrowForwardIcon />
                </Icon>
            </ToolAction>
        </ToolContent>
    </ToolCard>
);

export const AdminToolGridSkeleton: FC<{ count?: number }> = ({ count = 2 }) => (
    <AdminToolGrid aria-label={T._('Loading', 'Admin')} aria-busy='true'>
        {Array.from({ length: count }, (_, index) => (
            <ToolCard as='div' key={index}>
                <ToolContent $gap='12px'>
                    <SkeletonBlock $width='42px' $height='42px' $radius='10px' />
                    <SkeletonBlock $width='100px' $height='12px' />
                    <SkeletonBlock $width='70%' $height='20px' />
                    <SkeletonBlock $width='100%' $height='38px' />
                    <SkeletonBlock $width='90px' $height='16px' />
                </ToolContent>
            </ToolCard>
        ))}
    </AdminToolGrid>
);
